import type {
  ApiImageryCheckoutItemError,
  ApiImageryCheckoutItemErrorCode,
} from 'app/modules/Remote/api';
import * as apiUtils from 'app/utils/apiUtils';

import {SceneKeyParts, sceneKey} from './types';

export interface CheckoutItemRejection<T> {
  item: T;
  error: ApiImageryCheckoutItemError;
}

/**
 * What a failed checkout turned out to be. Checkout-level failures come back as bare message
 * strings, so they're recognized here once rather than string-matched at each call site.
 */
export type CheckoutFailure<T> =
  | {
      kind: 'itemRejections';
      rejections: CheckoutItemRejection<T>[];
      /** Errors naming a scene that isn't in the submitted set; shown but not actionable. */
      unmatched: ApiImageryCheckoutItemError[];
    }
  // The message carries no numbers, so the overage has to be computed client-side against the
  // contract's remaining NTE.
  | {kind: 'nteExceeded'; message: string}
  | {kind: 'mixedContracts'; message: string}
  | {kind: 'noContract'; message: string}
  | {kind: 'badRequest'; message: string}
  | {kind: 'unknown'; message: string; status?: number};

const ITEM_ERROR_CODES: ReadonlySet<string> = new Set<ApiImageryCheckoutItemErrorCode>([
  'sourceNotConfigured',
  'invalidSource',
  'invalidScene',
  'alreadyPurchased',
  'priceChanged',
  'acresMismatch',
  'sourceDisabled',
  'duplicateInCart',
  'notFound',
  'limitReached',
]);

// Checkout-level failures come back as prose, so each kind is recognized by the fragment of the
// message that identifies it.
const MESSAGE_KINDS = [
  ['Not enough quota available to order scene.', 'nteExceeded'],
  ['must bill to the same imagery contract', 'mixedContracts'],
  ['has no active imagery contract', 'noContract'],
] as const satisfies ReadonlyArray<readonly [string, CheckoutFailure<never>['kind']]>;

const isItemError = (value: unknown): value is ApiImageryCheckoutItemError => {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const error = value as Record<string, unknown>;
  return (
    typeof error.featureId === 'number' &&
    typeof error.sourceId === 'string' &&
    typeof error.sensingTime === 'string' &&
    typeof error.code === 'string' &&
    ITEM_ERROR_CODES.has(error.code)
  );
};

/**
 * Turns whatever the checkout call rejected with into one of the outcomes the checkout page
 * handles, pairing each per-item error with the item that caused it.
 */
export function normalizeCheckoutFailure<T extends SceneKeyParts>(
  thrown: unknown,
  submitted: T[]
): CheckoutFailure<T> {
  const apiError = (thrown ?? {}) as apiUtils.ApiResponseError;
  const status = apiError.status;
  const body = apiError.body as {errors?: unknown; error?: unknown} | undefined;

  const rawErrors = Array.isArray(body?.errors) ? body.errors : null;
  if (rawErrors) {
    // One bucket per scene, not one item: a source disabled on a cart holding the same scene twice
    // answers with an error per copy, and each has to land on its own row.
    const pending = new Map<string, T[]>();
    for (const item of submitted) {
      const key = sceneKey(item);
      const bucket = pending.get(key);
      if (bucket) {
        bucket.push(item);
      } else {
        pending.set(key, [item]);
      }
    }

    const rejections: CheckoutItemRejection<T>[] = [];
    const unmatched: ApiImageryCheckoutItemError[] = [];

    for (const raw of rawErrors) {
      if (!isItemError(raw)) {
        continue;
      }
      const item = pending.get(sceneKey(raw))?.shift();
      if (item) {
        rejections.push({item, error: raw});
      } else {
        unmatched.push(raw);
      }
    }
    if (rejections.length > 0 || unmatched.length > 0) {
      return {kind: 'itemRejections', rejections, unmatched};
    }
  }

  const message =
    (typeof body?.error === 'string' ? body.error : undefined) ??
    apiError.error?.message ??
    'Something went wrong submitting this order.';

  const matched = MESSAGE_KINDS.find(([fragment]) => message.includes(fragment));
  if (matched) {
    return {kind: matched[1], message};
  }
  if (status === 400) {
    return {kind: 'badRequest', message};
  }
  return {kind: 'unknown', message, status};
}
